// @flow
import React from 'react';
import styled from 'styled-components';

import reactNg from 'react-ng';
import { colors } from 'bench-style-constants';
import { Grid } from 'layout-core-components';
import { Label, Text, Icon, ToolTip } from 'primitive-components';

import TransactionSidebarBorderedSection from './TransactionSidebarBorderedSection';
import TransactionSidebarDataEntryRowContainer from './TransactionSidebarDataEntryRowContainer';
import type { JournalEntry } from './types';

const CommentHeader = styled.p`
  background: #f4f4f4;
  color: ${colors.dust};
  font-size: 10px;
  letter-spacing: 1.3px;
  margin: 0;
  padding: 8px 24px;
  position: sticky;
  text-transform: uppercase;
  top: 0;
  z-index: 1;
`;

const NewConversationWrapper = styled.div`
  position: relative;
  z-index: 0;
`;

const ClientCategorizationText = styled(Text)`
  padding-top: 16px;
`;

const CategorizationNoteLabelTooltipWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
`;

const TransactionSidebarDetailsTooltipIcon = styled(Icon)`
  fill: ${colors.dust};
  margin-left: 4px;
`;

type TransactionSidebarDetailsProps = {
  attachedTo: string,
  conversationId: ?string,
  currentLedgerId: ?string,
  documentAttachedTo: ?string,
  documentConversationId: ?string,
  transaction: JournalEntry,
  clientId: string,
};

export default function TransactionSidebarDetailsSlide({
  attachedTo,
  conversationId,
  currentLedgerId,
  documentAttachedTo,
  documentConversationId,
  transaction,
  clientId,
}: TransactionSidebarDetailsProps) {
  const clientCategorizationResponse = transaction.categorization
    ? transaction.categorization.categorizationUIClientComment
    : null;
  const clientCategorizationSelectedLedgerId =
    transaction.categorization &&
    transaction.categorization.selectedLedgerAccount &&
    transaction.categorization.selectedLedgerAccount.id;
  return (
    <Grid column="min-content min-content auto">
      <TransactionSidebarBorderedSection withPadding alwaysShowDivider>
        <Label>From Account</Label>
        {transaction.credits.map(dataEntry => (
          <TransactionSidebarDataEntryRowContainer
            dataEntryAmountInCents={dataEntry.amountInCents}
            dataEntryLedgerAccountId={dataEntry.ledgerAccount.id}
            transaction={transaction}
            clientId={clientId}
            ledgerNameTestId="sidebar-transactionLedgerFrom"
            clientCategorizationSelectedLedgerId={clientCategorizationSelectedLedgerId}
            key={dataEntry.id}
            currentLedgerId={currentLedgerId}
          />
        ))}
        <br />
        <Label>To Account</Label>
        {transaction.debits.map(dataEntry => (
          <TransactionSidebarDataEntryRowContainer
            dataEntryAmountInCents={dataEntry.amountInCents}
            transaction={transaction}
            clientId={clientId}
            ledgerNameTestId="sidebar-transactionLedgerTo"
            clientCategorizationSelectedLedgerId={clientCategorizationSelectedLedgerId}
            key={dataEntry.id}
            dataEntryLedgerAccountId={dataEntry.ledgerAccount.id}
            currentLedgerId={currentLedgerId}
          />
        ))}
      </TransactionSidebarBorderedSection>
      <TransactionSidebarBorderedSection>
        {conversationId && <CommentHeader>Transaction Comments</CommentHeader>}
        <NewConversationWrapper>
          {reactNg({ vm: { transactionId: transaction.id, attachedTo, conversationId } })`
            <bl-new-conversation
              attached-to="{{ vm.attachedTo }}"
              conversation-id="{{ vm.conversationId }}"
              class="padded-small"
              type="transaction">
            </bl-new-conversation>
          `}
        </NewConversationWrapper>
        {documentConversationId && <CommentHeader>Receipt Comments</CommentHeader>}
        <NewConversationWrapper>
          {reactNg({
            vm: { transactionId: transaction.id, documentAttachedTo, documentConversationId },
          })`
            <bl-new-conversation
              attached-to="{{ vm.documentAttachedTo }}"
              conversation-id="{{ vm.documentConversationId }}"
              hide-input="true"
              class="padded-small"
              type="transaction">
            </bl-new-conversation>
            <bl-document-preview document-id="vm.transactionId"></bl-document-preview>
          `}
        </NewConversationWrapper>
      </TransactionSidebarBorderedSection>
      {clientCategorizationResponse ? (
        <TransactionSidebarBorderedSection withPadding alwaysShowDivider>
          <CategorizationNoteLabelTooltipWrapper>
            <Label>Categorization Note</Label>
            <ToolTip
              contentAlignment="left"
              contentComponent={
                <Text size="small">
                  The note you left to clarify which <br /> category this transaction belongs to.
                </Text>
              }
              size="large"
              hPosition="center"
              vPosition="top"
            >
              <TransactionSidebarDetailsTooltipIcon icon="questionMark" size="small" />
            </ToolTip>
          </CategorizationNoteLabelTooltipWrapper>
          <ClientCategorizationText>{clientCategorizationResponse}</ClientCategorizationText>
        </TransactionSidebarBorderedSection>
      ) : (
        <div />
      )}
    </Grid>
  );
}

TransactionSidebarDetailsSlide.defaultProps = {
  currentLedgerId: '',
};
